import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { MonitoringModal } from './MonitoringCtoModal';
import { prepareMonitoringServiceOrder, prepareMonitoringBoxServiceOrder, createMonitoringServiceOrder } from '../../services/apiService';
import { getCtoLabel, getOltLabel } from '../../utils/networkMonitoring';

const diagnostic = client => [
    'Verificar / Corrigir Sinal',
    `Cliente no monitoramento: ${client.name || client.id}`,
    `Sinal Rx: ${client.signal ?? 'Sem leitura'} dBm`,
    `Sinal Tx: ${client.txPower ?? 'Sem leitura'} dBm`,
    `OLT: ${getOltLabel(client)}`,
    `CTO: ${getCtoLabel(client)}`,
    `PON: ${client.ponno ?? 'Não informado'} · Slot: ${client.slotno ?? 'Não informado'}`,
    `MAC/Serial: ${client.mac || 'Não informado'}`,
].join('\n');
const boxDiagnostic = box => [
    box.name.match(/\bCTO\s+(?:T\d+\s+)?[A-Z0-9]+(?:[./][A-Z0-9]+)*/i)?.[0] || box.name,
    `${String(box.logins.length).padStart(2, '0')} ${box.logins.length === 1 ? 'cliente afetado' : 'clientes afetados'}`,
    `Sinal médio Rx: ${box.average == null ? 'Sem leitura válida' : `${box.average.toFixed(2)} dBm`}`,
    `Última medição: ${new Date(box.measuredAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} (Brasília)`,
].join('\n');
const fieldClass = 'mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm disabled:opacity-60';

export default function MonitoringServiceOrderModal({ client, box, onClose, onCreated, theme }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [uncertain, setUncertain] = useState(false);
    const [result, setResult] = useState(null);
    const [subjectId, setSubjectId] = useState('');
    const [sectorId, setSectorId] = useState('');
    const [priority, setPriority] = useState('N');
    const [message, setMessage] = useState(() => box ? '' : diagnostic(client));
    const [structureId, setStructureId] = useState('');
    const [branchId, setBranchId] = useState('');
    const requestId = useRef(uuid());
    const submitting = useRef(false);
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let cancelled = false;
        setLoading(true); setError('');
        (box ? prepareMonitoringBoxServiceOrder(box.id) : prepareMonitoringServiceOrder(client.id)).then(prepared => {
            if (cancelled) return;
            setData(prepared);
            const subject = prepared.subjects.find(row => row.id === prepared.defaultSubjectId);
            setSubjectId(subject?.id || '');
            setSectorId(prepared.sectors.some(row => row.id === subject?.sectorId) ? subject.sectorId : '');
            setPriority(subject?.priority || 'N');
            setStructureId(prepared.box?.structureId || '');
            setBranchId(prepared.box?.branchId || '');
            setMessage(prepared.box ? boxDiagnostic(prepared.box) : [subject?.description, diagnostic(client)].filter(Boolean).join('\n\n'));
        }).catch(e => { if (!cancelled) setError(e.response?.data?.message || 'Não foi possível carregar os dados do IXC.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [client, box, attempt]);
    const changeSubject = value => {
        const subject = data.subjects.find(row => row.id === value);
        setSubjectId(value);
        setSectorId(data.sectors.some(row => row.id === subject?.sectorId) ? subject.sectorId : '');
        setPriority(subject?.priority || 'N');
    };
    const submit = async event => {
        event.preventDefault();
        if (submitting.current || uncertain || result) return;
        submitting.current = true;
        setBusy(true); setError('');
        try {
            const created = await createMonitoringServiceOrder({ ...(box ? { boxId: box.id, structureId, branchId } : { clientId: client.id }), subjectId, sectorId, priority, message, confirmed: true, requestId: requestId.current });
            setResult(created);
            onCreated?.(created);
        } catch (e) {
            const ambiguous = !e.response || e.response.data?.uncertain === true;
            setUncertain(ambiguous);
            setError(e.response?.data?.message || 'Não foi possível confirmar a abertura. Confira no IXC antes de tentar novamente.');
            if (!ambiguous) requestId.current = uuid();
        } finally { submitting.current = false; setBusy(false); }
    };
    const locked = busy || uncertain || Boolean(result);
    return <MonitoringModal title="Abrir O.S. no IXC" onClose={onClose} busy={busy} theme={theme} explicitClose>
        <div className="w-full space-y-4">
            {loading && <p role="status">{box ? 'Carregando CTO e cadastros do IXC…' : 'Carregando cliente e cadastros do IXC…'}</p>}
            {error && <p role="alert" className="rounded-lg bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700 dark:text-red-300">{error}</p>}
            {!loading && !data && <button type="button" className="btn-secondary" onClick={() => setAttempt(value => value + 1)}>Tentar carregar novamente</button>}
            {result && <div role="status" className="rounded-lg bg-green-50 dark:bg-green-950/30 p-4 text-green-800 dark:text-green-200">
                <strong>O.S. {result.orderId} aberta e confirmada no IXC.</strong>
                {result.protocol && <p className="mt-1 text-sm">Protocolo: {result.protocol}</p>}
            </div>}
            {!loading && data && <form onSubmit={submit} className="space-y-4">
                {data.box ? <div className="rounded-xl bg-slate-50 dark:bg-slate-900/60 p-4 text-sm space-y-2">
                    <p className="font-semibold">{data.box.name} (ID {data.box.boxId})</p>
                    <p>Tipo: Estrutura própria · {data.box.logins.length} login(s) afetado(s)</p>
                    <p>Latitude: {data.box.coordinates.lat} · Longitude: {data.box.coordinates.lng}</p>
                    <p>Sinal médio Rx: {data.box.average == null ? 'Sem leitura válida' : `${data.box.average.toFixed(2)} dBm`}</p>
                    <p>Última medição: {new Date(data.box.measuredAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} (Brasília)</p>
                    <p>Logins afetados: {data.box.logins.map(row => `${row.login} (#${row.id})`).join(', ')}</p>
                    <p>Caixas de atendimento: {data.box.boxId}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">A descrição abaixo será enviada à O.S. A CTO e os logins serão vinculados nos campos próprios do IXC.</p>
                    <label className="block">Estrutura
                        <select required className={fieldClass} value={structureId} disabled={locked} onChange={event => setStructureId(event.target.value)}>
                            <option value="">Selecione a estrutura da cidade da CTO</option>
                            {data.box.structures.map(row => <option key={row.id} value={row.id}>{row.id} — {row.name}</option>)}
                        </select>
                    </label>
                    <label className="block">Filial
                        <select required className={fieldClass} value={branchId} disabled={locked} onChange={event => setBranchId(event.target.value)}>
                            <option value="">Selecione a filial</option>
                            {data.box.branches.map(row => <option key={row.id} value={row.id}>{row.id} — {row.name}</option>)}
                        </select>
                    </label>
                </div> : <div className="rounded-xl bg-slate-50 dark:bg-slate-900/60 p-4 text-sm space-y-1">
                    <p className="font-semibold">{data.installation.customerName}</p>
                    <p>Cliente #{data.installation.customerId} · Login {data.installation.login} (#{data.installation.loginId})</p>
                    <p>Contrato: {data.installation.contractId || 'Sem contrato vinculado'} · Filial: {data.installation.branchName}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">O.S. aberta, sem agendamento ou técnico atribuído</p>
                </div>}
                {data.installation?.address && <section aria-label="Endereço que será enviado à O.S." className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-sm">
                    <h4 className="font-semibold">Endereço da instalação</h4>
                    <p className="mt-1 mb-3 text-xs text-slate-500 dark:text-slate-400">{data.installation.address.sourceLabel}</p>
                    <dl className="grid gap-3 sm:grid-cols-2">
                        {[
                            ['Cidade', data.installation.address.cityName || data.installation.address.cityId],
                            ['Bairro', data.installation.address.neighborhood], ['Endereço', data.installation.address.street],
                            ['Número', data.installation.address.number], ['Complemento', data.installation.address.complement],
                            ['CEP', data.installation.address.postalCode], ['Referência', data.installation.address.reference],
                            ['Bloco', data.installation.address.block], ['Apartamento', data.installation.address.apartment],
                        ].map(([label, value]) => <div key={label}><dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt><dd className="break-words">{value || 'Não informado no IXC'}</dd></div>)}
                    </dl>
                </section>}
                <label className="block text-sm font-medium">Assunto
                    <select className={fieldClass} value={subjectId} required disabled={locked || Boolean(box)} onChange={event => changeSubject(event.target.value)}>
                        <option value="">Selecione um assunto</option>
                        {data.subjects.map(row => <option key={row.id} value={row.id}>{row.id} — {row.name}</option>)}
                    </select>
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block text-sm font-medium">Setor
                        <select className={fieldClass} value={sectorId} required disabled={locked || Boolean(box)} onChange={event => setSectorId(event.target.value)}>
                            <option value="">Selecione um setor</option>
                            {data.sectors.map(row => <option key={row.id} value={row.id}>{row.id} — {row.name}</option>)}
                        </select>
                    </label>
                    <label className="block text-sm font-medium">Prioridade
                        <select className={fieldClass} value={priority} disabled={locked} onChange={event => setPriority(event.target.value)}>
                            {[['B', 'Baixa'], ['N', 'Normal'], ['A', 'Alta'], ['C', 'Crítica']].map(([value, name]) => <option key={value} value={value}>{name}</option>)}
                        </select>
                    </label>
                </div>
                <label className="block text-sm font-medium">Descrição do serviço
                    <textarea className={fieldClass} rows={8} maxLength={10000} required value={message} disabled={locked} onChange={event => setMessage(event.target.value)} />
                </label>
                {!result && <button type="submit" disabled={locked || !subjectId || !sectorId || !message.trim() || (box && (!structureId || !branchId))} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg px-4 py-3 disabled:opacity-50">
                    {busy ? 'Abrindo O.S.…' : 'Confirmar e abrir O.S.'}
                </button>}
            </form>}
        </div>
    </MonitoringModal>;
}
